// Constraint-expression scanner — Slice 21 helper for indexBuilder.
//
// DEFAULT and CHECK constraint bodies are bare expressions, not statements,
// so the parse-and-walk pipeline used for views / inline-TVFs / scalar
// functions does not apply. A token-level walk that skips comments + strings
// + operators + keywords gives us reliable column + function-call extraction
// across all five engines without standing up an expression-mode parser.
//
// `mode === 'check'` emits column refs for non-keyword identifiers and routine
// refs for any identifier followed by `(`. Sibling-column refs use
// `referenceKind: 'referenced-in-expression'` per the slice criteria.
// `mode === 'default'` emits routine refs only — for identifiers followed by
// `(` OR for known parameterless built-ins (CURRENT_TIMESTAMP / SYSDATE /
// etc.). Bare non-function identifiers in DEFAULT bodies are typically
// literals / constants and get skipped.

import { rangeFromOffsets, type LineIndex } from '../../sql-grammar/document/lineIndex';
import type { ParseOptions, QualifiedName, SqlRange } from '../../sql-grammar/model';
import { skipContextFor, trySkipNonCode } from '../../sql-resolver/classify/tokenSkip';
import { readQualifiedName, type ReadQualifiedNameResult } from '../../sql-resolver/scope/identifiers';

import type { IdentifierIndex, IdentifierKind, ReferenceKind, ReferenceRecord } from '../types';

import type { PersistedObjectListing } from './objectBodyFetcher';

export type ConstraintExpressionMode = 'default' | 'check';

// SQL keywords / null + boolean literals / type names that should never
// surface as column refs when scanning a CHECK expression. Compared
// case-insensitively (always against the upper-cased identifier).
const KEYWORDS: ReadonlySet<string> = new Set([
  'AND', 'OR', 'NOT', 'NULL', 'TRUE', 'FALSE',
  'IS', 'IN', 'EXISTS', 'BETWEEN', 'LIKE', 'ILIKE', 'SIMILAR', 'TO', 'ESCAPE',
  'ALL', 'ANY', 'SOME', 'ARRAY',
  'CASE', 'WHEN', 'THEN', 'ELSE', 'END',
  'CAST', 'AS', 'CONVERT', 'COLLATE',
  'CHECK', 'DEFAULT', 'CONSTRAINT', 'COLUMN', 'NOCHECK', 'WITH',
  'INT', 'INTEGER', 'BIGINT', 'SMALLINT', 'TINYINT',
  'DECIMAL', 'NUMERIC', 'NUMBER',
  'FLOAT', 'REAL', 'DOUBLE', 'PRECISION',
  'VARCHAR', 'CHAR', 'CHARACTER', 'TEXT', 'NVARCHAR', 'NCHAR',
  'BLOB', 'BINARY', 'VARBINARY', 'BIT', 'BOOLEAN', 'BOOL',
  'DATE', 'TIME', 'DATETIME', 'DATETIME2', 'TIMESTAMP',
  'UUID', 'JSON', 'JSONB', 'BYTEA', 'VARYING',
  'N',
]);

// Parameterless built-in time / user functions that count as routine refs
// in a DEFAULT expression even with no `(`. Each is uppercase to match the
// case-folded identifier compare inside `handleIdentifier`.
const PARAMETERLESS_BUILTINS: ReadonlySet<string> = new Set([
  'CURRENT_TIMESTAMP', 'CURRENT_DATE', 'CURRENT_TIME',
  'LOCALTIME', 'LOCALTIMESTAMP',
  'SYSDATE', 'SYSTIMESTAMP',
  'CURRENT_USER', 'SESSION_USER', 'SYSTEM_USER', 'USER',
]);

const SKIP_BYTES: ReadonlySet<string> = new Set([
  ' ', '\t', '\n', '\r',
  ',', ';', '=', '+', '-', '*', '/', '%',
  '<', '>', '!', '?', '|', '&', '~', '^', '@',
  '(', ')', '[', ']',
]);

export function walkConstraintExpression(
  source: string,
  byteOffset: number,
  options: ParseOptions,
  listing: PersistedObjectListing,
  mode: ConstraintExpressionMode,
  lineIdx: LineIndex,
  index: IdentifierIndex,
): void {
  // Disable backtick / bracket auto-skip so quoted identifiers stay reachable
  // by `readQualifiedName`. Comments, single-quoted strings, and PG dollar
  // tags still get skipped. `"` is only a string delimiter on MySQL; on every
  // other engine it's a quoted identifier and must reach the identifier
  // reader, so we run `trySkipNonCode` only when MySQL.
  const skipCtx = {
    ...skipContextFor(options),
    supportsBacktick: false,
    supportsBracket: false,
  };
  const treatDoubleQuoteAsString = options.engine === 'mysql';
  const len = source.length;
  let i = 0;
  while (i < len) {
    const c = source[i];
    if (SKIP_BYTES.has(c)) { i += 1; continue; }
    if (c !== '"' || treatDoubleQuoteAsString) {
      const skipped = trySkipNonCode(source, i, len, skipCtx);
      if (skipped >= 0 && skipped > i) { i = skipped; continue; }
    }
    if (isDigit(c)) { i = consumeNumber(source, i, len); continue; }
    if (c === ':' && source[i + 1] === ':') {
      i = consumePgCast(source, i + 2, len, options);
      continue;
    }
    const qn = readQualifiedName(source, i, len, options);
    if (!qn) { i += 1; continue; }
    handleIdentifier(qn, source, byteOffset, listing, mode, lineIdx, index);
    i = qn.range.endOffset > i ? qn.range.endOffset : i + 1;
  }
}

function handleIdentifier(
  qn: ReadQualifiedNameResult,
  source: string,
  byteOffset: number,
  listing: PersistedObjectListing,
  mode: ConstraintExpressionMode,
  lineIdx: LineIndex,
  index: IdentifierIndex,
): void {
  const upper = (qn.qualified.foldedName ?? qn.qualified.name).toUpperCase();
  const isCall = peekIsOpenParen(source, qn.range.endOffset);

  // SQL keywords / type names take precedence over the function-call shape:
  // `CHECK (...)` and `DEFAULT (...)` would otherwise look like calls.
  if (!qn.qualified.quoted && KEYWORDS.has(upper)) return;

  if (isCall) {
    emitRef(listing, qn.qualified, 'routine', qn.range, 'call', byteOffset, lineIdx, index);
    return;
  }
  if (PARAMETERLESS_BUILTINS.has(upper)) {
    emitRef(listing, qn.qualified, 'routine', qn.range, 'call', byteOffset, lineIdx, index);
    return;
  }
  if (mode === 'check') {
    emitRef(
      listing,
      canonicalizeColumn(qn.qualified),
      'column',
      qn.range,
      'referenced-in-expression',
      byteOffset,
      lineIdx,
      index,
    );
  }
  // mode === 'default' + bare non-function identifier: treated as literal,
  // skipped per slice criteria (DEFAULTs primarily reference functions).
}

function isDigit(c: string): boolean {
  return c >= '0' && c <= '9';
}

function consumeNumber(source: string, i: number, len: number): number {
  let j = i + 1;
  while (j < len) {
    const c = source[j];
    if (!(isDigit(c) || c === '.' || c === 'e' || c === 'E' || c === '+' || c === '-')) break;
    j++;
  }
  return j;
}

function consumePgCast(source: string, i: number, len: number, options: ParseOptions): number {
  let j = i;
  while (j < len && /\s/.test(source[j])) j++;
  const typeName = readQualifiedName(source, j, len, options);
  if (!typeName) return j;
  let k = typeName.range.endOffset;
  while (k < len && (source[k] === '[' || source[k] === ']' || source[k] === ' ' || source[k] === '\t')) k++;
  return k;
}

function peekIsOpenParen(source: string, from: number): boolean {
  let k = from;
  while (k < source.length && /\s/.test(source[k])) k++;
  return source[k] === '(';
}

function canonicalizeColumn(qn: QualifiedName): QualifiedName {
  return { name: qn.name, raw: qn.name, quoted: qn.quoted, foldedName: qn.foldedName };
}

function emitRef(
  listing: PersistedObjectListing,
  identifier: QualifiedName,
  identifierKind: IdentifierKind,
  rawRange: SqlRange,
  referenceKind: ReferenceKind,
  byteOffset: number,
  lineIdx: LineIndex,
  index: IdentifierIndex,
): void {
  const adjusted = rangeFromOffsets(
    lineIdx,
    rawRange.startOffset + byteOffset,
    rawRange.endOffset + byteOffset,
  );
  const record: ReferenceRecord = {
    objectKind: listing.objectKind,
    objectName: listing.objectName,
    identifier,
    identifierKind,
    range: adjusted,
    referenceKind,
    parseStatus: 'ok',
  };
  index.record(record);
}
